/**
 * Enhanced Screen Reader Support
 * Comprehensive screen reader announcements and ARIA live region management
 */

export interface EnhancedScreenReaderOptions {
  enableLogging?: boolean;
  maxAnnouncementHistory?: number;
  debounceDelay?: number;
  respectUserPreferences?: boolean;
}

export interface AnnouncementRecord {
  message: string;
  priority: 'polite' | 'assertive';
  timestamp: number;
}

export class EnhancedScreenReader {
  private options: Required<EnhancedScreenReaderOptions>;
  private announcementHistory: AnnouncementRecord[] = [];
  private debounceTimers: Map<string, NodeJS.Timeout> = new Map();
  private liveRegions: Map<string, HTMLElement> = new Map();
  private isScreenReaderActive = false;

  constructor(options: EnhancedScreenReaderOptions = {}) {
    this.options = {
      enableLogging: options.enableLogging ?? false,
      maxAnnouncementHistory: options.maxAnnouncementHistory ?? 50,
      debounceDelay: options.debounceDelay ?? 100,
      respectUserPreferences: options.respectUserPreferences ?? true,
    };

    this.detectScreenReader();
    this.createLiveRegions();
  }

  private detectScreenReader(): void {
    // Check user agent for screen readers
    const userAgent = navigator.userAgent.toLowerCase();
    const screenReaderIndicators = ['nvda', 'jaws', 'dragon', 'zoomtext'];
    
    this.isScreenReaderActive = screenReaderIndicators.some(indicator => 
      userAgent.includes(indicator)
    );

    // Check for reduced motion preference (often indicates assistive technology)
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.isScreenReaderActive = true;
    }
  }

  private createLiveRegions(): void {
    const regions = [
      { id: 'sr-polite', ariaLive: 'polite' },
      { id: 'sr-assertive', ariaLive: 'assertive' },
      { id: 'sr-status', role: 'status' },
      { id: 'sr-alert', role: 'alert' },
    ];

    regions.forEach(({ id, ariaLive, role }) => {
      let region = document.getElementById(id);
      
      if (!region) {
        region = document.createElement('div');
        region.id = id;
        region.className = 'sr-only';
        
        if (ariaLive) region.setAttribute('aria-live', ariaLive);
        if (role) region.setAttribute('role', role);
        
        // Screen reader only styles
        Object.assign(region.style, {
          position: 'absolute',
          width: '1px',
          height: '1px',
          padding: '0',
          margin: '-1px',
          overflow: 'hidden',
          clip: 'rect(0, 0, 0, 0)',
          whiteSpace: 'nowrap',
          border: '0',
        });

        document.body.appendChild(region);
      }
      
      this.liveRegions.set(id, region);
    });
  }

  public announce(
    message: string, 
    priority: 'polite' | 'assertive' = 'polite',
    options: { debounce?: boolean } = {}
  ): void {
    if (!message.trim()) return;

    // Skip announcement if respecting user preferences and no screen reader detected
    if (this.options.respectUserPreferences && !this.isScreenReaderActive) {
      return;
    }

    const regionId = priority === 'assertive' ? 'sr-assertive' : 'sr-polite';
    
    if (options.debounce !== false && this.options.debounceDelay > 0) {
      this.debouncedAnnounce(regionId, message, priority);
    } else {
      this.immediateAnnounce(regionId, message, priority);
    }
  }

  private debouncedAnnounce(regionId: string, message: string, priority: 'polite' | 'assertive'): void {
    const existingTimer = this.debounceTimers.get(regionId);
    if (existingTimer) {
      clearTimeout(existingTimer);
    }

    const timer = setTimeout(() => {
      this.immediateAnnounce(regionId, message, priority);
      this.debounceTimers.delete(regionId);
    }, this.options.debounceDelay);

    this.debounceTimers.set(regionId, timer);
  }

  private immediateAnnounce(regionId: string, message: string, priority: 'polite' | 'assertive'): void {
    const region = this.liveRegions.get(regionId);
    if (!region) return;

    // Check if region is active (has aria-live attribute)
    if (!region.hasAttribute('aria-live') && !region.hasAttribute('role')) {
      return; // Region is disabled, skip announcement
    }

    // Clear and set message - use proper screen reader announcement pattern
    region.textContent = '';
    // Force reflow to ensure clear operation is processed
    void region.offsetHeight;
    region.textContent = message;

    // Track announcement
    this.addToHistory(message, priority);

    if (this.options.enableLogging) {
      console.log(`[Screen Reader] ${priority}: ${message}`);
    }
  }

  public announceStatus(message: string): void {
    // Skip announcement if respecting user preferences and no screen reader detected
    if (this.options.respectUserPreferences && !this.isScreenReaderActive) {
      return;
    }

    const region = this.liveRegions.get('sr-status');
    if (region) {
      region.textContent = message;
      this.addToHistory(message, 'polite');
    }
  }

  public announceAlert(message: string): void {
    // Skip announcement if respecting user preferences and no screen reader detected
    if (this.options.respectUserPreferences && !this.isScreenReaderActive) {
      return;
    }

    const region = this.liveRegions.get('sr-alert');
    if (region) {
      region.textContent = message;
      this.addToHistory(message, 'assertive');
    }
  }

  public announceLoading(isLoading: boolean, context?: string): void {
    const message = isLoading 
      ? context ? `Loading ${context}...` : 'Loading...'
      : context ? `Loading complete for ${context}` : 'Loading complete';
    
    this.announceStatus(message);
  }

  public announceValidation(isValid: boolean, errorCount?: number, formName?: string): void {
    let message: string;
    
    if (isValid) {
      message = formName ? `Form validation passed for ${formName}` : 'Form validation passed';
    } else {
      const errors = errorCount ? ` ${errorCount} errors` : '';
      const form = formName ? ` in ${formName}` : '';
      message = `Form validation failed${errors}${form}`;
    }
    
    this.announceAlert(message);
  }

  public announceNavigation(destination: string, type: 'link' | 'button' = 'link'): void {
    this.announce(`Navigating to ${destination}`, 'assertive');
  }

  public announceModal(isOpen: boolean, modalName?: string): void {
    const message = isOpen 
      ? modalName ? `Dialog opened: ${modalName}` : 'Dialog opened'
      : 'Dialog closed';
    
    this.announce(message, 'assertive');
  }

  public announceTable(rows: number, columns: number, caption?: string): void {
    const message = caption 
      ? `Table with ${rows} rows and ${columns} columns: ${caption}`
      : `Table with ${rows} rows and ${columns} columns`;
    
    this.announce(message, 'polite');
  }

  public setRegionActive(regionType: 'polite' | 'assertive', active: boolean): void {
    const regionId = regionType === 'assertive' ? 'sr-assertive' : 'sr-polite';
    const region = this.liveRegions.get(regionId);
    
    if (region) {
      if (active) {
        region.setAttribute('aria-live', regionType);
      } else {
        region.removeAttribute('aria-live');
      }
    }
  }

  private addToHistory(message: string, priority: 'polite' | 'assertive'): void {
    const record: AnnouncementRecord = {
      message,
      priority,
      timestamp: Date.now(),
    };

    this.announcementHistory.push(record);

    // Limit history size
    if (this.announcementHistory.length > this.options.maxAnnouncementHistory) {
      this.announcementHistory.shift();
    }
  }

  public getAnnouncementHistory(): AnnouncementRecord[] {
    return [...this.announcementHistory];
  }

  public clearHistory(): void {
    this.announcementHistory = [];
  }

  public isActive(): boolean {
    return this.isScreenReaderActive;
  }

  public destroy(): void {
    // Clear debounce timers
    this.debounceTimers.forEach(timer => clearTimeout(timer));
    this.debounceTimers.clear();

    // Clear history
    this.clearHistory();

    // Note: We don't remove live regions as they might be shared
    // across multiple instances
  }
}

// Singleton instance for convenience
let screenReaderInstance: EnhancedScreenReader | null = null;

export function getScreenReader(options?: EnhancedScreenReaderOptions): EnhancedScreenReader {
  if (!screenReaderInstance) {
    screenReaderInstance = new EnhancedScreenReader(options);
  }
  return screenReaderInstance;
}

export function announceToScreenReader(
  message: string, 
  priority: 'polite' | 'assertive' = 'polite'
): void {
  const screenReader = getScreenReader();
  screenReader.announce(message, priority);
}